import type { CommentMember } from '@nimbalyst/collab-bundle/editor';
import { listMembers, type TeamMember } from '../api';
import type { TeamJwt } from '../auth/teamJwt';

type GetTeamJwt = (options?: { forceRefresh?: boolean }) => Promise<TeamJwt>;

interface OrgRosterEntry {
  members: readonly TeamMember[];
  /** The caller's own org role, as the server reports it. Null until resolved. */
  callerRole: string | null;
  promise: Promise<readonly TeamMember[]>;
}

const orgRosters = new Map<string, OrgRosterEntry>();

/** Start or join the single REST roster request for an organization. */
export function loadOrgRoster(
  orgId: string,
  getTeamJwt: GetTeamJwt,
): Promise<readonly TeamMember[]> {
  const cached = orgRosters.get(orgId);
  if (cached) return cached.promise;

  const entry: OrgRosterEntry = {
    members: [],
    callerRole: null,
    promise: Promise.resolve([]),
  };
  entry.promise = getTeamJwt()
    .then((jwt) => listMembers(jwt, orgId))
    .then(({ members, callerRole }) => {
      entry.members = members;
      entry.callerRole = callerRole;
      return entry.members;
    })
    .catch((error) => {
      if (orgRosters.get(orgId) === entry) orgRosters.delete(orgId);
      throw error;
    });
  orgRosters.set(orgId, entry);
  return entry.promise;
}

/** Return the last resolved REST roster without triggering network work. */
export function getOrgRoster(orgId: string): readonly TeamMember[] {
  return orgRosters.get(orgId)?.members ?? [];
}

/** The caller's own org role, or null while the roster request is in flight. */
export function getOrgCallerRole(orgId: string): string | null {
  return orgRosters.get(orgId)?.callerRole ?? null;
}

/**
 * Whether an org role may author document comments.
 *
 * Comment threads live in the document's Y.Doc, so this has to agree with the
 * server's *document write* gate, not with its message-posting gate. That gate
 * is `TeamRoom.resolveDocumentAccess`, which intersects the org role's action
 * set with the project grant's: `owner`/`admin` always carry `comment`,
 * `viewer`/`guest` are capped at read and can never reach it whatever their
 * project grant says, and `member` carries it as long as the project grant does
 * (`project-editor` is what an org member is seeded with).
 *
 * The project grant is not knowable from the roster, so this answers on the org
 * role alone. It is exact at both ends that matter -- a viewer is refused, an
 * owner is allowed -- and only a `member` whose project grant was manually
 * downgraded can still be offered an affordance the server refuses. That
 * member's whole document is read-only, so their first rejected write flips
 * `serverAccess` and the bundle withdraws the affordance.
 *
 * An unrecognized or absent role fails closed.
 */
export function canOrgRoleAuthorComments(role: string | null): boolean {
  return role === 'owner' || role === 'admin' || role === 'member';
}

export function memberDisplayName(member: TeamMember): string {
  // Email is identity/contact data, not a display-name fallback. Legacy or
  // incomplete rows use the opaque team member id until Stytch provides a name.
  return member.name || member.memberId;
}

/** Synchronous comment roster accessor; self-exclusion lives only here. */
export function getCommentMembers(
  orgId: string,
  selfMemberId: string,
): CommentMember[] {
  return getOrgRoster(orgId)
    .filter((member) => member.memberId !== selfMemberId)
    .map((member) => ({
      userId: member.memberId,
      name: memberDisplayName(member),
    }));
}
